import { Col, Icon, Tappable, Text, useTheme } from "@rally/ui-kit";
import { type ReactNode, useState } from "react";
import { say } from "../../localization/say.ts";
import type { Person } from "../composer/saying.ts";
import { BusyPanel } from "./busy-panel.tsx";
import { Face, LiveDot, nameFor, saidOf } from "./busy-parts.tsx";
import { type Busy, longestOf, whoIsBusy } from "./doing.ts";
import { useNow } from "./tool-frame.tsx";
import { elapsedSaid, type Someone } from "./working.ts";

const FACES = 3;

const A_ROW_HOLDS = 34;

function Stack({ rows, known }: { rows: readonly Busy[]; known: Map<string, Person> }) {
  const theme = useTheme();
  return (
    <Col style={{ flexDirection: "row", alignItems: "center" }}>
      {rows.slice(0, FACES).map((row, at) => (
        <Col
          key={row.account}
          style={{
            marginLeft: at === 0 ? 0 : -8,
            borderRadius: 999,
            borderWidth: 2,
            borderColor: theme.color["bg.surface"],
          }}
        >
          <Face row={row} known={known} size={20} />
        </Col>
      ))}
    </Col>
  );
}

export function BusyRow({
  conversation,
  busy,
  known,
  routing = null,
}: {
  conversation: string;
  busy: { thinking: Someone[]; unanswered: Someone[]; typing: Someone[] };
  known: Map<string, Person>;

  routing?: ReactNode;
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const [stopped, setStopped] = useState<Map<string, string>>(new Map());
  const rows = whoIsBusy(busy, stopped);

  const now = useNow(rows.length > 0);
  const oldest = longestOf(rows);
  const clock = elapsedSaid(oldest, now);
  const one = rows.length === 1 ? rows[0] : null;
  const live = rows.some((row) => row.run !== null && !row.why);

  if (rows.length === 0 && !routing) return null;

  if (rows.length === 0) {
    return (
      <Col
        testID="record-busy"
        style={{
          flexDirection: "row",
          justifyContent: "flex-end",
          marginTop: theme.space[3],
          marginBottom: theme.space[3],
        }}
      >
        {routing}
      </Col>
    );
  }

  return (
    <Col
      testID="record-busy"
      style={{ gap: theme.space[2], marginTop: theme.space[3], marginBottom: theme.space[3] }}
    >
      {open ? (
        <BusyPanel
          conversation={conversation}
          rows={rows}
          known={known}
          now={now}
          onStopped={(account, why) => setStopped((was) => new Map(was).set(account, why))}
        />
      ) : null}
      <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
        <Tappable
          accessibilityRole="button"
          accessibilityLabel={open ? say("busy.close") : say("busy.open")}
          accessibilityState={{ expanded: open }}
          testID="busy-row"
          onPress={() => setOpen((was) => !was)}
          style={{
            flex: 1,
            minWidth: 0,
            flexDirection: "row",
            alignItems: "center",
            gap: theme.space[2],
            paddingVertical: theme.space[2],
            paddingHorizontal: theme.space[2],
            borderRadius: theme.radius.md,
          }}
        >
          {one ? <Face row={one} known={known} size={20} /> : <Stack rows={rows} known={known} />}
          <LiveDot on={live} />
          <Col
            style={{ flex: 1, minWidth: 0, flexDirection: "row", alignItems: "center", gap: theme.space[2] }}
          >
            {one ? (
              <Text typeName="caption" color="text.secondary" numberOfLines={1} testID="busy-row-who">
                {nameFor(one, known)}
              </Text>
            ) : null}
            <Text typeName="caption" color="text.tertiary" numberOfLines={1} testID="busy-row-what">
              {one ? saidOf(one, A_ROW_HOLDS) : say("busy.many")(rows.length)}
            </Text>
          </Col>
          {clock ? (
            <Text typeName="caption" color="text.tertiary" testID="busy-row-clock">
              {clock}
            </Text>
          ) : null}
          <Icon name={open ? "chevron-down" : "chevron-up"} size={14} color="text.tertiary" />
        </Tappable>
        {routing}
      </Col>
    </Col>
  );
}
